/**
 * reducers/trips.ts
 * desc: message trip reporting
 * note: read-only analytics and metrics
 */

import type { Reducer } from '@reduxjs/toolkit';
import type { TripsAction } from '../actions/trips';
import type { TripDto, TripInfoDto } from '../../types/api/trips';
import type { PageInfo } from '../../types/api/paging';

export interface TripsList {
    tripsInfo: TripInfoDto[];
    // empty until the first page arrives
    page: Partial<PageInfo>;
    asOf?: string;
}

export interface TripsState {
    isLoading: {
        getTrip: boolean;
        getTrips: boolean;
        getTripsHistory: boolean;
        restartTrip: boolean;
        stopTrip: boolean;
    };
    // an empty object until a trip is fetched
    detail: TripDto | Record<string, never>;
    list: TripsList;
    tripsHistoryList: TripInfoDto[];
}

const INITIAL_STATE: TripsState = {
    isLoading: {
        getTrip: false,
        getTrips: false,
        getTripsHistory: false,
        restartTrip: false,
        stopTrip: false,
    },
    detail: {},
    list: {
        tripsInfo: [],
        page: {},
    },
    tripsHistoryList: []
};


function tripsReducer(state: TripsState = INITIAL_STATE, action: TripsAction): TripsState {
    switch (action.type) {
        // CLEAR Trips (Reset All)
        case 'CLEAR_TRIPS':
            return _clearTrips();

        // GET Trip (Instance)
        case 'GET_TRIP_ERROR':
            return _getTripError(state);
        case 'GET_TRIP_REQUEST':
            return _getTripRequest(state);
        case 'GET_TRIP_SUCCESS':
            return _getTripSuccess(state, action.payload);

        // GET Trips (Collection)
        case 'GET_TRIPS_ERROR':
            return _getTripsError(state);
        case 'GET_TRIPS_REQUEST':
            return _getTripsRequest(state);
        case 'GET_TRIPS_SUCCESS':
            return _getTripsSuccess(state, action.payload);

        // GET Trips History
        case 'GET_TRIPS_HISTORY_ERROR':
            return _getTripsHistoryError(state);
        case 'GET_TRIPS_HISTORY_REQUEST':
            return _getTripsHistoryRequest(state);
        case 'GET_TRIPS_HISTORY_SUCCESS':
            return _getTripsHistorySuccess(state, action.payload);

        // RESTART Trip (Instance)
        case 'RESTART_TRIP_ERROR':
            return _restartTripError(state);
        case 'RESTART_TRIP_REQUEST':
            return _restartTripRequest(state);
        case 'RESTART_TRIP_SUCCESS':
            return _restartTripSuccess(state);

        // RESTART Trips Bulk
        case 'RESTART_TRIPS_ERROR':
            return _restartTripError(state);
        case 'RESTART_TRIPS_REQUEST':
            return _restartTripRequest(state);
        case 'RESTART_TRIPS_SUCCESS':
            return _restartTripSuccess(state);

        // STOP Trip (Instance)
        case 'STOP_TRIP_ERROR':
            return _stopTripError(state);
        case 'STOP_TRIP_REQUEST':
            return _stopTripRequest(state);
        case 'STOP_TRIP_SUCCESS':
            return _stopTripSuccess(state, action.payload);

        // STOP Trips Bulk
        case 'STOP_TRIPS_ERROR':
            return _stopTripError(state);
        case 'STOP_TRIPS_REQUEST':
            return _stopTripRequest(state);
        case 'STOP_TRIPS_SUCCESS':
            return _stopTripsSuccess(state);

        default:
            return state;
    }
}



//*************************************************
//*****   CLEAR Trips (Reset All)   ***************
//*************************************************
function _clearTrips(): TripsState {
    return INITIAL_STATE;
}



//*************************************************
//*****   GET Trip (Instance)   *******************
//*************************************************
function _getTripError(state: TripsState): TripsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getTrip: false,
        },
    };
}

function _getTripRequest(state: TripsState): TripsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getTrip: true,
        },
    };
}

function _getTripSuccess(state: TripsState, payload: TripDto): TripsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getTrip: false,
        },
        detail: payload,
    };
}



//*************************************************
//*****   GET Trips (Collection)   ****************
//*************************************************
function _getTripsError(state: TripsState): TripsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getTrips: false,
        },
    };
}

function _getTripsRequest(state: TripsState): TripsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getTrips: true,
        },
    };
}

function _getTripsSuccess(state: TripsState, payload: TripsList): TripsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getTrips: false,
        },
        list: payload,
    };
}


//*************************************************
//*****   GET Trips History        ****************
//*************************************************
function _getTripsHistoryError(state: TripsState): TripsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getTripsHistory: false,
        },
    };
}

function _getTripsHistoryRequest(state: TripsState): TripsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getTripsHistory: true,
        },
    };
}

function _getTripsHistorySuccess(state: TripsState, payload: TripsList): TripsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getTripsHistory: false,
        },
        tripsHistoryList: payload.tripsInfo || [],
    };
}



//*************************************************
//*****   RESTART Trip / Trips Bulk   *************
//*************************************************
function _restartTripError(state: TripsState): TripsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            restartTrip: false,
        },
    };
}

function _restartTripRequest(state: TripsState): TripsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            restartTrip: true,
        },
    };
}

function _restartTripSuccess(state: TripsState): TripsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            restartTrip: false,
        }
    };
}



//*************************************************
//*****   STOP Trip                ****************
//*************************************************
function _stopTripError(state: TripsState): TripsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            stopTrip: false,
        },
    };
}

function _stopTripRequest(state: TripsState): TripsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            stopTrip: true,
        },
    };
}

function _stopTripSuccess(state: TripsState, payload: { tripId: string }): TripsState {
    const { tripId } = payload;

    const updatedTrips = state.list.tripsInfo.map((x): TripInfoDto => {
        if (x.id === tripId) {
            return {
                ...x,
                status: 'STOPPED',
            };
        }
        return x;
    });

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            stopTrip: false,
        },
        list: {
            ...state.list,
            tripsInfo: updatedTrips,
        }
    };
}



//*************************************************
//*****   STOP Trips bulk          ****************
//*************************************************
function _stopTripsSuccess(state: TripsState): TripsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            stopTrip: false,
        }
    };
}

export default tripsReducer as Reducer<TripsState>;
